import { useId, useRef, type KeyboardEvent, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  EmptyState,
  Input,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  Skeleton,
  Tooltip,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ChevronIcon, CloseIcon, SearchIcon, TagIcon } from '@/components/catalog/icons';
import { cx } from '@/lib/cx';

/**
 * The Product Insights kit — one page template for all seven products (owner,
 * 2026-10-19: "a complete ui ux overhaul such that it is intuitive and easily
 * usable"). A user who learns one Product Insights page knows them all:
 *
 *   PiPageLayout  header · facts line · PiProductNav
 *   PiToolbar     page-wide filters, chips + Reset, trailing view toggle
 *   PiStatGrid    at most four headline tiles; a clickable tile is a filter
 *   PiTabs        Overview · tables, with counts
 *   body          PiSection panels; states PiLoading / PiError / PiEmpty
 *
 * Rules carried here so no page re-decides them:
 * - Built only from `components/ui` primitives and design tokens. No helper
 *   text: `facts` is for facts about the data only.
 * - `PiDeniedPage` is the refusal the pages rendered before the overhaul, copy
 *   verbatim. The ability check stays on each page.
 * - `PiTabs` is a real `tablist` (roving tabindex, arrow keys, Home/End). The
 *   product nav and the tab bar scroll sideways inside themselves at 320px.
 * - `null` / `undefined` counts render "—", never 0.
 * - `PiProductNav` links carry nothing from the current URL: each product has
 *   its own filter keys.
 */

export type PiProductKey =
  | 'fab'
  | 'scholarships'
  | 'events'
  | 'lae'
  | 'icr'
  | 'visa-tutor'
  | 'challenges'
  | 'school-profiles';

/**
 * SEED's funnel stages, as universities.seedglobaleducation.com groups its
 * services (owner 2026-10-19): Attract · Nurture · Convert.
 */
export type PiStage = 'Attract' | 'Nurture' | 'Convert';
export const PI_STAGES: readonly PiStage[] = ['Attract', 'Nurture', 'Convert'];

/** Nav order, labels and stages as in the Product Insights group of `lib/nav.tsx`. */
export const PI_PRODUCTS: readonly { key: PiProductKey; label: string; to: string; stage: PiStage }[] = [
  { key: 'events', label: 'Events', to: '/product-insights/events', stage: 'Attract' },
  { key: 'school-profiles', label: 'School Profiles', to: '/product-insights/school-profiles', stage: 'Attract' },
  { key: 'challenges', label: 'Challenge Hub', to: '/product-insights/challenges', stage: 'Attract' },
  { key: 'icr', label: 'In-Country Representation', to: '/product-insights/icr', stage: 'Nurture' },
  { key: 'lae', label: 'Lead & Application Engagement', to: '/product-insights/lae', stage: 'Nurture' },
  { key: 'fab', label: 'Funnel Analytics & BI', to: '/product-insights/fab', stage: 'Convert' },
  { key: 'scholarships', label: 'Scholarships', to: '/product-insights/scholarships', stage: 'Convert' },
  { key: 'visa-tutor', label: 'AI Visa Tutor', to: '/product-insights/visa-tutor', stage: 'Convert' },
];

/**
 * SEED services with no Product Insights page yet (owner 2026-10-19): shown in
 * the switcher as inert pills with a "Soon" badge, matching the sidebar rows.
 */
export const PI_COMING_SOON: readonly { label: string; stage: PiStage }[] = [
  { label: 'Digital Advertising', stage: 'Attract' },
  { label: 'Edumate', stage: 'Convert' },
];

// ── Formatting ───────────────────────────────────────────────────────────────

const COUNT_FORMAT = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const PCT_FORMATS = new Map<number, Intl.NumberFormat>();

/** Grouped integer; null / undefined / non-finite → "—". */
export function piCount(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return COUNT_FORMAT.format(n);
}

/** A 0–1 rate as a percentage: 0.423 → "42%"; null / undefined → "—". */
export function piPct(rate: number | null | undefined, digits = 0): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return '—';
  const safeDigits = Math.min(Math.max(Math.trunc(digits), 0), 20);
  let format = PCT_FORMATS.get(safeDigits);
  if (!format) {
    format = new Intl.NumberFormat(undefined, {
      style: 'percent',
      minimumFractionDigits: safeDigits,
      maximumFractionDigits: safeDigits,
    });
    PCT_FORMATS.set(safeDigits, format);
  }
  return format.format(rate);
}

/** The `aria-sort` value for a `TH` that holds a `PiSortHeader`. */
export function piAriaSort(
  active: boolean,
  direction: 'asc' | 'desc',
): 'ascending' | 'descending' | 'none' {
  if (!active) return 'none';
  return direction === 'asc' ? 'ascending' : 'descending';
}

// ── Page frame ───────────────────────────────────────────────────────────────

export function PiProductNav({ active }: { active: PiProductKey }): ReactElement {
  // One scrolling row, clustered by stage: a quiet stage label, then its pills.
  return (
    <nav aria-label="Product Insights" className="min-w-0">
      <div className="flex max-w-full items-center gap-3 overflow-x-auto pb-0.5">
        {PI_STAGES.map((stage, index) => {
          const products = PI_PRODUCTS.filter((product) => product.stage === stage);
          const soon = PI_COMING_SOON.filter((entry) => entry.stage === stage);
          if (products.length === 0 && soon.length === 0) return null;
          return (
            <div key={stage} className="flex shrink-0 items-center gap-1.5">
              {index > 0 && <span aria-hidden="true" className="mr-1.5 h-5 w-px bg-border" />}
              <span id={`pi-stage-${stage}`} className="text-xs font-medium text-fg-subtle">
                {stage}
              </span>
              <ul aria-labelledby={`pi-stage-${stage}`} className="flex items-center gap-1.5">
                {products.map((product) => {
                  const current = product.key === active;
                  return (
                    <li key={product.key} className="shrink-0">
                      <Link
                        to={product.to}
                        aria-current={current ? 'page' : undefined}
                        className={cx(
                          'inline-flex h-9 items-center whitespace-nowrap rounded-full border px-3 text-sm transition-colors duration-150 ease-out motion-reduce:transition-none max-sm:h-11',
                          current
                            ? 'border-transparent bg-accent-brand-soft font-semibold text-fg'
                            : 'border-border bg-surface font-medium text-fg-muted hover:border-hairline-strong hover:text-fg',
                        )}
                      >
                        {product.label}
                      </Link>
                    </li>
                  );
                })}
                {soon.map((entry) => (
                  <li key={entry.label} className="shrink-0">
                    <span
                      aria-disabled="true"
                      className="inline-flex h-9 cursor-default items-center gap-1.5 whitespace-nowrap rounded-full border border-dashed border-border px-3 text-sm font-medium text-fg-subtle max-sm:h-11"
                    >
                      {entry.label}
                      <Badge tone="neutral">Soon</Badge>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </nav>
  );
}

export function PiPageLayout({
  product,
  title,
  facts,
  actions,
  children,
}: {
  product: PiProductKey;
  title: string;
  facts?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="grid min-w-0 gap-4">
      <div className="grid min-w-0 gap-3">
        <PageHeader title={title} description={facts} actions={actions} className="pb-0" />
        <PiProductNav active={product} />
      </div>
      <div className="grid min-w-0 gap-4">{children}</div>
    </div>
  );
}

/** Today's refusal, copy verbatim. Mount it when `ability.canRead` is false. */
export function PiDeniedPage({ product, title }: { product: PiProductKey; title: string }): ReactElement {
  const headingId = `pi-${product}-denied-heading`;
  return (
    <div className="grid min-w-0 gap-4">
      <PageHeader title={title} />
      <Panel aria-labelledby={headingId}>
        <PanelHeader id={headingId} title={title} />
        <EmptyState
          icon={<TagIcon />}
          title="Not available to your account"
          description="Your permission set does not include this. An administrator can change that."
        />
      </Panel>
    </div>
  );
}

// ── Toolbar ──────────────────────────────────────────────────────────────────

export function PiToolbar({
  children,
  chips,
  trailing,
}: {
  children: ReactNode;
  chips?: ReactNode;
  trailing?: ReactNode;
}): ReactElement {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-surface px-3 py-2">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{children}</div>
        {trailing != null && trailing !== false && (
          <div className="ml-auto flex min-w-0 flex-wrap items-center gap-2">{trailing}</div>
        )}
      </div>
      {chips != null && chips !== false && (
        <div className="mt-2 min-w-0 border-t border-border pt-2">{chips}</div>
      )}
    </div>
  );
}

export function PiFilterChips({
  chips,
  onReset,
}: {
  chips: { key: string; label: ReactNode; onRemove: () => void }[];
  onReset?: () => void;
}): ReactElement | null {
  const baseId = useId();
  if (chips.length === 0) return null;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5" role="group" aria-label="Active filters">
      {chips.map((chip, index) => {
        const labelId = `${baseId}-chip-${index}`;
        const buttonId = `${baseId}-remove-${index}`;
        return (
          <span
            key={chip.key}
            className="inline-flex h-7 max-w-full items-center gap-0.5 rounded-full border border-border bg-surface-sunken pr-0.5 pl-2.5 text-xs text-fg-secondary max-sm:h-9"
          >
            <span id={labelId} className="truncate">
              {chip.label}
            </span>
            <Tooltip label="Remove" describes={false}>
              <button
                id={buttonId}
                type="button"
                onClick={chip.onRemove}
                aria-labelledby={`${buttonId} ${labelId}`}
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-fg-muted transition-colors duration-150 ease-out hover:bg-surface-hover hover:text-fg motion-reduce:transition-none max-sm:h-8 max-sm:w-8"
              >
                <span className="sr-only">Remove</span>
                <CloseIcon className="h-3 w-3 shrink-0" />
              </button>
            </Tooltip>
          </span>
        );
      })}
      {onReset && (
        <Button variant="ghost" size="sm" onClick={onReset}>
          Reset
        </Button>
      )}
    </div>
  );
}

export function PiSearch({
  value,
  onChange,
  label,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  className?: string;
}): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const clear = () => {
    onChange('');
    inputRef.current?.focus();
  };
  return (
    <div className={cx('min-w-0 w-full sm:w-64', className)}>
      <Input
        ref={inputRef}
        type="text"
        inputMode="search"
        enterKeyHint="search"
        autoComplete="off"
        aria-label={label}
        placeholder="Search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && value !== '') {
            event.preventDefault();
            onChange('');
          }
        }}
        leadingIcon={<SearchIcon />}
        trailingSlot={
          value !== '' ? (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Clear search"
              onClick={clear}
            >
              <CloseIcon className="h-3.5 w-3.5 shrink-0" />
            </Button>
          ) : undefined
        }
      />
    </div>
  );
}

// ── Headline tiles ───────────────────────────────────────────────────────────

export function PiStatGrid({ children, className }: { children: ReactNode; className?: string }): ReactElement {
  return <div className={cx('grid min-w-0 grid-cols-2 gap-3 lg:grid-cols-4', className)}>{children}</div>;
}

export function PiStat({
  label,
  value,
  detail,
  onClick,
  active = false,
  loading = false,
}: {
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
  onClick?: () => void;
  active?: boolean;
  loading?: boolean;
}): ReactElement {
  const body = (
    <>
      <span className="block truncate text-xs font-medium text-fg-muted">{label}</span>
      {loading ? (
        <Skeleton className="mt-1.5 h-7 w-20 max-w-full" />
      ) : (
        <span className="mt-1 block break-words text-xl font-semibold leading-tight tracking-[-0.01em] text-fg tabular-nums">
          {value}
        </span>
      )}
      {detail != null && detail !== false && (
        <span className="mt-1 block break-words text-xs text-fg-muted">{detail}</span>
      )}
    </>
  );

  const shape = 'block w-full min-w-0 rounded-lg border px-4 py-3 text-left max-sm:px-3';

  if (onClick) {
    return (
      <button
        type="button"
        aria-pressed={active}
        onClick={onClick}
        className={cx(
          shape,
          'transition-colors duration-150 ease-out motion-reduce:transition-none',
          active
            ? 'border-accent-brand bg-accent-brand-tint'
            : 'border-border bg-surface hover:border-hairline-strong hover:bg-surface-hover',
        )}
      >
        {body}
      </button>
    );
  }

  return <div className={cx(shape, 'border-border bg-surface')}>{body}</div>;
}

// ── Tabs ─────────────────────────────────────────────────────────────────────

export function PiTabs<T extends string>({
  tabs,
  value,
  onChange,
  ariaLabel,
}: {
  tabs: readonly { key: T; label: string; count?: number | null }[];
  value: T;
  onChange: (key: T) => void;
  ariaLabel: string;
}): ReactElement {
  const baseId = useId();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = tabs.findIndex((tab) => tab.key === value);
  // A value outside the list still leaves one tab in the tab order.
  const focusableIndex = selectedIndex === -1 ? 0 : selectedIndex;

  const activate = (index: number) => {
    const tab = tabs[index];
    if (!tab) return;
    tabRefs.current[index]?.focus();
    if (tab.key !== value) onChange(tab.key);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = tabs.length - 1;
    if (last < 0) return;
    let next: number | null = null;
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    activate(next);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      className="flex max-w-full min-w-0 items-end gap-1 overflow-x-auto border-b border-border"
    >
      {tabs.map((tab, index) => {
        const selected = tab.key === value;
        return (
          <button
            key={tab.key}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            id={`${baseId}-tab-${index}`}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={index === focusableIndex ? 0 : -1}
            onClick={() => {
              if (!selected) onChange(tab.key);
            }}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cx(
              '-mb-px inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 text-sm transition-colors duration-150 ease-out motion-reduce:transition-none max-sm:h-11',
              selected
                ? 'border-accent-brand font-semibold text-fg'
                : 'border-transparent font-medium text-fg-muted hover:border-hairline-strong hover:text-fg',
            )}
          >
            <span>{tab.label}</span>
            {typeof tab.count === 'number' && (
              <span className="text-xs font-normal text-fg-muted tabular-nums">{piCount(tab.count)}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Sections and states ──────────────────────────────────────────────────────

export function PiSection({
  title,
  count,
  actions,
  children,
  id,
  flush = false,
}: {
  title: ReactNode;
  count?: number | null;
  actions?: ReactNode;
  children: ReactNode;
  id?: string;
  flush?: boolean;
}): ReactElement {
  const generatedId = useId();
  const headingId = id ? `${id}-heading` : `${generatedId}-heading`;
  return (
    <Panel id={id} aria-labelledby={headingId} className="min-w-0">
      <PanelHeader
        id={headingId}
        title={
          <>
            {title}
            {typeof count === 'number' && (
              <span className="ml-1.5 font-normal text-fg-muted tabular-nums">{piCount(count)}</span>
            )}
          </>
        }
        actions={actions}
      />
      <PanelBody density={flush ? 'flush' : 'default'} className="min-w-0">
        {children}
      </PanelBody>
    </Panel>
  );
}

const TABLE_CELL_WIDTHS = ['w-2/5', 'w-1/6', 'w-1/6', 'w-1/6'] as const;

export function PiLoading({
  variant = 'table',
  rows = 6,
}: {
  variant?: 'table' | 'tiles' | 'chart';
  rows?: number;
}): ReactElement {
  const count = Math.max(1, Math.trunc(rows));
  let shape: ReactNode;

  if (variant === 'tiles') {
    shape = (
      <PiStatGrid>
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="rounded-lg border border-border bg-surface px-4 py-3 max-sm:px-3">
            <Skeleton className="h-3 w-24 max-w-full" />
            <Skeleton className="mt-2 h-7 w-20 max-w-full" />
          </div>
        ))}
      </PiStatGrid>
    );
  } else if (variant === 'chart') {
    shape = (
      <div className="grid gap-3 px-4 py-3">
        {Array.from({ length: count }, (_, index) => (
          <div key={index} className="grid gap-1.5">
            <Skeleton className="h-3 w-32 max-w-full" />
            <Skeleton className={cx('h-5', index % 3 === 0 ? 'w-full' : index % 3 === 1 ? 'w-3/4' : 'w-1/2')} />
          </div>
        ))}
      </div>
    );
  } else {
    shape = (
      <div className="min-w-0">
        <div className="flex gap-4 border-b border-border bg-surface-sunken px-4 py-2.5">
          {TABLE_CELL_WIDTHS.map((width, index) => (
            <Skeleton key={index} className={cx('h-3', width)} />
          ))}
        </div>
        {Array.from({ length: count }, (_, index) => (
          <div key={index} className="flex gap-4 border-b border-border px-4 py-3 last:border-b-0">
            {TABLE_CELL_WIDTHS.map((width, cell) => (
              <Skeleton key={cell} className={cx('h-4', width)} />
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div role="status" aria-busy="true" className="min-w-0">
      <span className="sr-only">Loading</span>
      {shape}
    </div>
  );
}

export function PiError({ message, onRetry }: { message: string; onRetry?: () => void }): ReactElement {
  return (
    <Notice
      tone="danger"
      role="alert"
      action={
        onRetry ? (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Retry
          </Button>
        ) : undefined
      }
    >
      {message}
    </Notice>
  );
}

export function PiEmpty({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}): ReactElement {
  return <EmptyState icon={icon} title={title} description={description} action={action} />;
}

// ── Tables and cards ─────────────────────────────────────────────────────────

/**
 * The button inside a sortable `TH`. The `TH` carries `aria-sort` (see
 * `piAriaSort`); this only draws the label and the direction chevron.
 */
export function PiSortHeader({
  label,
  active,
  direction,
  onClick,
  align = 'left',
}: {
  label: string;
  active: boolean;
  direction: 'asc' | 'desc';
  onClick: () => void;
  align?: 'left' | 'right';
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'group/sort inline-flex max-w-full items-center gap-1 rounded-sm font-medium transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none',
        align === 'right' && 'flex-row-reverse text-right',
        active && 'text-fg',
      )}
    >
      <span>{label}</span>
      <span
        aria-hidden="true"
        className={cx(
          'inline-flex shrink-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
          active
            ? 'opacity-100'
            : 'opacity-0 group-hover/sort:opacity-50 group-focus-visible/sort:opacity-50',
        )}
      >
        <ChevronIcon direction={active && direction === 'asc' ? 'up' : 'down'} />
      </span>
    </button>
  );
}

/** Chevron toggle for an inline expanded row. `label` names the row's detail. */
export function PiExpandButton({
  expanded,
  onClick,
  label,
}: {
  expanded: boolean;
  onClick: () => void;
  label: string;
}): ReactElement {
  return (
    <Button variant="ghost" size="sm" iconOnly aria-label={label} aria-expanded={expanded} onClick={onClick}>
      <ChevronIcon direction={expanded ? 'down' : 'right'} className="h-4 w-4 shrink-0" />
    </Button>
  );
}

/** The stacked card list shown below `md` in place of a table. */
export function PiCardList<T>({
  items,
  getKey,
  render,
}: {
  items: readonly T[];
  getKey: (t: T) => string;
  render: (t: T) => ReactNode;
}): ReactElement {
  return (
    <ul className="grid min-w-0 gap-2 md:hidden">
      {items.map((item) => (
        <li key={getKey(item)} className="min-w-0 rounded-lg border border-border bg-surface p-3">
          {render(item)}
        </li>
      ))}
    </ul>
  );
}

export function PiCardField({ label, value }: { label: string; value: ReactNode }): ReactElement {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-3 text-xs">
      <span className="shrink-0 text-fg-muted">{label}</span>
      <span className="min-w-0 text-right break-words text-fg tabular-nums">{value}</span>
    </div>
  );
}
